// src/pages/Folder/EditFolder.tsx

import {
  AccountBalanceTwoTone,
  AddTwoTone,
  CancelTwoTone,
  DeleteTwoTone,
  EditLocationTwoTone,
  FaceTwoTone,
  GroupTwoTone,
  MemoryTwoTone,
  MoreVertTwoTone,
  SaveTwoTone,
  ScheduleTwoTone,
} from '@mui/icons-material'
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { Storage } from 'aws-amplify'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ContractId, ContractRecord, SensorRecord } from '@/types/domain'
import {
  AirConditioning,
  AreaInSqm,
  FolderId,
  FolderPool,
  FolderSingle,
  Latitude,
  Longitude,
  NumberOfEmployees,
  OfficeHoursRecord,
  RoomsMode,
  UsableArea,
} from '@/types/domain/folder'
import { UserPool, UserRecord } from '@/types/domain/user'

import {
  FILTER_ACTIVE_USER,
  isValidLength,
  getInitials,
  SORT,
  SVGStatus,
} from '../../api'
import defaultImage from '../../assets/img/image_placeholder.jpg'
import {
  AM,
  AMItem,
  DlgFolderImage,
  DlgSensorAdd,
  CardFolderSensor,
  HStack,
  KPage,
  KSelect,
  KTextField,
  Section,
  DlgDelete,
} from '../../components'
import { languageConstants as LC } from '../../constants'
import {
  FolderStatus,
  useBridgeId,
  useCoachPool,
  useContractApi,
  useFolder,
  useFolderApi,
  useFolderPool,
  useFolderSensors,
  useFolderStatus,
  useFolderUsers,
  useSensor,
  useUserPool,
} from '../../hooks'

const roomModes = mode => {
  switch (mode) {
    case 'rooms':
      return 'Zimmer'
    case 'employees':
      return 'Mitarbeiter'
    default:
      return 'Mitarbeiter'
  }
}

const getSeverity = status => {
  switch (status) {
    case 'aktiv':
      return 'success'

    case 'abgelaufen':
    case 'geplant':
      return 'warning'

    case 'ungültig':
      return 'error'

    default:
      return 'info'
  }
}

const normalizeTime = time => {
  if (!time) return '00:00:00'

  // wenn "7:30"
  if (time.length === 7) time = '0' + time // → "07:30"

  // wenn noch keine Sekunden dabei
  if (time.length === 5) time = time + ':00' // → "07:30:00"

  return time
}

export const EditFolder = () => {
  const navigate = useNavigate()

  const { id, section } = useParams()

  const fId: FolderId = Number(id)

  const fPool: FolderPool = useFolderPool()
  const {
    remove: removeFolder,
    save: saveFolder,
    update: updateFolder,
  } = useFolderApi()

  const uPool: UserPool = useUserPool()
  const coachPool = useCoachPool()

  const { remove: removeContract, update: updateContract } = useContractApi()

  const editMode = id ? true : false

  const [tab, setTab] = useState(Number(section) || 0)

  const [folderSensors, setFolderSensors] = useState([])
  const [folderUsers, setFolderUsers] = useState([])

  const fStatus: FolderStatus = useFolderStatus(fId)

  const [folder, setFolder] = useState({
    elevator: 1,
    escalator: 1,
    name: '',
    name_short: '',
    street: '',
    zip: '',
    city: '',
    country: '',
    latitude: '',
    longitude: '',
    parent_id: 0,
    image_url: '',
    first_aid_kit: '',
    extinguisher: '',
    area_in_sqm: '',
    number_of_employees: '',
    usable_area: '',
    hired_out_area: '',
    temperature_controlled_area: '',
    none_temperature_controlled_area: '',
    rooms_mode: '',
    roomsModeSelect: null,
    kind_of_use: '',
    air_conditioning: '',
    push_sensors: [],
    target_value_annual_electricity_consumption: '',
    target_value_annual_gas_consumption: '',
    total_consumption_available: 1,
  })

  const [officeHours, setOfficeHours] = useState([
    { day_of_week: 1, start_time: '00:00:00', end_time: '00:00:00', closed: 1 },
    { day_of_week: 2, start_time: '00:00:00', end_time: '00:00:00', closed: 1 },
    { day_of_week: 3, start_time: '00:00:00', end_time: '00:00:00', closed: 1 },
    { day_of_week: 4, start_time: '00:00:00', end_time: '00:00:00', closed: 1 },
    { day_of_week: 5, start_time: '00:00:00', end_time: '00:00:00', closed: 1 },
    { day_of_week: 6, start_time: '00:00:00', end_time: '00:00:00', closed: 1 },
    { day_of_week: 7, start_time: '00:00:00', end_time: '00:00:00', closed: 1 },
  ])

  const [installations, setInstallations] = useState([])

  const [exceptionDate, setExceptionDate] = useState([])

  const [contractId, setContractId] = useState<ContractId>(null)
  const [contractData, setContractData] = useState<ContractRecord>(null)

  const [status, setStatus] = useState('')
  const [duration, setDuration] = useState(0)

  const [showModal, setModal] = useState(false)

  const [roleFilter, setRoleFilter] = useState('')
  const [displayFilter, setDisplayFilter] = useState('')

  const fData: FolderSingle = useFolder(fId)

  const fSensors: SensorRecord[] = useFolderSensors(fId)
  useEffect(() => setFolderSensors(fSensors), [fSensors])

  const fUsers: UserRecord[] = useFolderUsers(fId)
  useEffect(() => setFolderUsers(fUsers), [fUsers])

  useEffect(() => {
    if (!editMode || !fData) return

    setFolder(prev => ({
      ...prev,

      elevator: fData.elevator,
      escalator: fData.escalator,

      name: fData.name ?? '',
      name_short: fData.name_short ?? '',
      street: fData.street ?? '',
      zip: fData.zip ?? '',
      city: fData.city ?? '',
      country: fData.country ?? '',

      latitude: String(fData.latitude ?? ''),
      longitude: String(fData.longitude ?? ''),

      area_in_sqm: String(fData.area_in_sqm ?? ''),
      usable_area: String(fData.usable_area ?? ''),
      hired_out_area: String(fData.hired_out_area ?? ''),
      temperature_controlled_area: String(
        fData.temperature_controlled_area ?? ''
      ),
      none_temperature_controlled_area: String(
        fData.none_temperature_controlled_area ?? ''
      ),

      number_of_employees: String(fData.number_of_employees ?? ''),

      rooms_mode: fData.rooms_mode,
      roomsModeSelect: {
        value: fData.rooms_mode,
        label: roomModes(fData.rooms_mode),
      },

      kind_of_use: fData.kind_of_use ?? '',
      air_conditioning: fData.air_conditioning ?? '',

      target_value_annual_electricity_consumption: String(
        fData.target_value_annual_electricity_consumption ?? ''
      ),
      target_value_annual_gas_consumption: String(
        fData.target_value_annual_gas_consumption ?? ''
      ),

      total_consumption_available: Number(
        fData.total_consumption_available ?? 1
      ),

      image_url: fData.image_url ?? '',
      first_aid_kit: fData.first_aid_kit ?? '',
      extinguisher: fData.extinguisher ?? '',

      push_sensors: [],
    }))
  }, [id, editMode, fData])

  const bridgeId = useBridgeId(fId)
  const bridgeData = useSensor(bridgeId)

  useEffect(() => {
    if (!bridgeData?.contracts) return

    const { contracts } = bridgeData

    const cRecord = contracts[0]
    if (!cRecord) return

    setContractId(cRecord.entity_id)
  }, [bridgeData])

  useEffect(() => {
    if (!editMode || !fData) return

    setContractData(fData.contract)

    setInstallations(fData.installations)

    const data: OfficeHoursRecord[] = fData.office_hours

    setOfficeHours(data.filter(f => f.exception === 0))
    setExceptionDate(data.filter(f => f.exception === 1))
  }, [fData, editMode])

  useEffect(() => {
    if (!contractData) return

    const date_begin = contractData?.date_begin
    const date_expire = contractData?.date_expire
    const isValid = date => date instanceof Date && !isNaN(date.getTime())

    const BEGIN = new Date(date_begin)
    const EXPIRE = new Date(date_expire)
    const NOW = new Date()
    let status

    if (NOW < BEGIN) status = 'geplant'
    if (NOW > EXPIRE) status = 'abgelaufen'
    if (NOW > BEGIN && NOW < EXPIRE) status = 'aktiv'
    if (BEGIN > EXPIRE) status = 'ungültig'
    if (!isValid(BEGIN) || !isValid(EXPIRE)) status = 'ungültig'

    setStatus(status)
    setDuration((EXPIRE.getTime() - BEGIN.getTime()) / (24 * 60 * 60 * 1000))
  }, [contractData])

  const onSubmit = action => {
    const sensor_id = folderSensors.map(item => ({ value: item.entity_id }))
    const user_id = folderUsers.map(item => ({ value: item.entity_id }))

    const record = {
      exceptionDate,
      folder: {
        ...folder,
        rooms_mode: folder.rooms_mode as 'employees' | 'rooms',
        latitude: Number(folder.latitude),
        longitude: Number(folder.longitude),
        area_in_sqm: Number(folder.area_in_sqm),
        usable_area: Number(folder.usable_area),
        hired_out_area: Number(folder.hired_out_area),
        temperature_controlled_area: Number(folder.temperature_controlled_area),
        none_temperature_controlled_area: Number(
          folder.none_temperature_controlled_area
        ),
        number_of_employees: Number(folder.number_of_employees),
        target_value_annual_electricity_consumption: Number(
          folder.target_value_annual_electricity_consumption
        ),
        target_value_annual_gas_consumption: Number(
          folder.target_value_annual_gas_consumption
        ),
        sensor_id,
        user_id,
        push_sensors: [],
        folder_installations: installations,
      },
      id: fId,
      officeHours,
    }

    const { date_begin, date_expire, description, name, primary_coach_id } =
      contractData

    const contractRecord = {
      name,
      description,
      date_begin,
      date_expire,
      primary_coach_id,
      sensor_id,
      user_id,
    }

    switch (action) {
      case 'save':
        saveFolder(record, contractRecord)
        break

      case 'update':
        updateFolder(record)
        updateContract(contractId, contractRecord)
        break

      case 'delete':
        removeFolder(record.id)
        removeContract(contractId)
        navigate('/folder')
        break

      default:
        break
    }
  }

  const [actionMenu, setActionMenu] = useState(null)
  const [confirmDialog, setConfirmDialog] = useState(false)
  const [openProfilePicture, setOpenProfilePicture] = useState(false)

  const deleteInstallation = (item, key) => {
    const install = installations.slice()
    install[key]['delete_installation'] = 1

    setInstallations(install)
  }

  const changeOfficeHours = (record, key) => {
    const updated = [...officeHours]
    updated[key] = record

    setOfficeHours(updated)
  }
  const NO_SENSOR_IN_OBJECT = folderSensors.length === 0

  const onUpdateFolderImage = image_url => {
    /* img_url kann string oder object sein */
    /* -> uploadAws */
    setFolder(prev => ({ ...prev, image_url }))
  }

  const onChangeFolderSensors = event =>
    setFolderSensors(prev => [...prev, event.target.value.attributes])

  const onChangeName = event => {
    const name = event.target.value

    setFolder(prev => ({ ...prev, name }))
    setContractData(prev => ({ ...prev, name }))
  }

  const onChangeShortName = event => {
    const name_short = event.target.value

    setFolder(prev => ({ ...prev, name_short }))
  }

  const onChangeParentFolder = event => {
    const parent_id: FolderId = event.target.value

    setFolder(prev => ({ ...prev, parent_id }))
  }

  const onChangeCoach = event => {
    const primary_coach_id = event.target.value

    setContractData(prev => ({ ...prev, primary_coach_id }))
  }

  const onChangeDescription = event => {
    const description = event.target.value

    setContractData(prev => ({ ...prev, description }))
  }

  const onToggleElevator = event => {
    const elevator = event.target.checked ? 1 : 0

    setFolder(prev => ({ ...prev, elevator }))
  }

  const onToggleEscalator = event => {
    const escalator = event.target.checked ? 1 : 0

    setFolder(prev => ({ ...prev, escalator }))
  }

  const onToggleConsumptionAvailable = event => {
    const total_consumption_available = event.target.checked ? 1 : 0

    setFolder(prev => ({ ...prev, total_consumption_available }))
  }

  const onChangeStreet = event => {
    const street = event.target.value

    setFolder(prev => ({ ...prev, street }))
  }

  const onChangeZip = event => {
    const zip = event.target.value

    setFolder(prev => ({ ...prev, zip }))
  }

  const onChangeCity = event => {
    const city = event.target.value

    setFolder(prev => ({ ...prev, city }))
  }

  const onChangeCountry = event => {
    const country = event.target.value

    setFolder(prev => ({ ...prev, country }))
  }

  const onChangeLatitude = event => {
    const latitude: Latitude = event.target.value

    setFolder(prev => ({ ...prev, latitude: String(latitude) }))
  }

  const onChangeLongitude = event => {
    const longitude: Longitude = event.target.value

    setFolder(prev => ({ ...prev, longitude: String(longitude) }))
  }

  const onChangeEmployees = event => {
    const number_of_employees: NumberOfEmployees = event.target.value

    setFolder(prev => ({
      ...prev,
      number_of_employees: String(number_of_employees),
    }))
  }

  const onChangeRoomsMode = event => {
    const rooms_mode: RoomsMode = event.target.value
    const roomsModeSelect = event.target.value

    setFolder(prev => ({ ...prev, rooms_mode, roomsModeSelect }))
  }

  const onChangeAreaInSqm = event => {
    const area_in_sqm: AreaInSqm = event.target.value

    setFolder(prev => ({ ...prev, area_in_sqm: String(area_in_sqm) }))
  }

  const onChangeUsableArea = event => {
    const usable_area: UsableArea = event.target.value

    setFolder(prev => ({ ...prev, usable_area: String(usable_area) }))
  }

  const onChangeHiredOutArea = event => {
    const hired_out_area = event.target.value

    setFolder(prev => ({ ...prev, hired_out_area }))
  }

  const onChangeNoneTemperatureControlledArea = event => {
    const none_temperature_controlled_area = event.target.value

    setFolder(prev => ({ ...prev, none_temperature_controlled_area }))
  }

  const onChangeTemperatureControlledArea = event => {
    const temperature_controlled_area = event.target.value

    setFolder(prev => ({ ...prev, temperature_controlled_area }))
  }

  const onAddInstallation = event => {
    event.stopPropagation()

    setInstallations([...installations, { name: '', entity_id: 0 }])
  }

  const onChangeKindOfUse = event => {
    const kind_of_use = event.target.value

    setFolder(prev => ({ ...prev, kind_of_use }))
  }

  const onChangeAirConditioning = event => {
    const air_conditioning: AirConditioning = event.target.value

    setFolder(prev => ({ ...prev, air_conditioning }))
  }

  const onChangeContractStart = event => {
    const date_begin = event.target.value

    setContractData(prev => ({ ...prev, date_begin }))
  }

  const onChangeContractEnd = event => {
    const date_expire = event.target.value

    setContractData(prev => ({ ...prev, date_expire }))
  }

  const onCloseAddSensor = () => setModal(!showModal)
  const onCloseAM = () => setActionMenu(null)
  const onCloseDelete = () => setConfirmDialog(false)
  const onCloseFolderImage = () => setOpenProfilePicture(false)

  const onOpenAddSensor = () => setModal(true)
  const onOpenAM = event => setActionMenu(event.currentTarget)
  const onOpenDelete = () => setConfirmDialog(true)
  const onOpenFolderImage = () => setOpenProfilePicture(true)

  const onConfirmDelete = () => onSubmit('delete')

  const onAddFolder = () => navigate('/folder/new')
  const onStoreFolder = () => onSubmit(!editMode ? 'save' : 'update')

  const gotoPerformance = () => navigate(`/folder/${fId}/home/performance`)
  const gotoInstallation = () => navigate(`/folder/${fId}/home/installation`)
  const gotoCorrelation = () => navigate(`/folder/${fId}/home/correlation`)
  const gotoSecurity = () => navigate(`/folder/${fId}/home/security`)
  const gotoFeeling = () => navigate(`/folder/${fId}/home/feeling`)

  const amData = [
    {
      caption: 'Neu',
      icon: <AddTwoTone />,
      fn: onAddFolder,
      id: 0,
    },
    {
      caption: 'Speichern',
      disabled:
        status === 'ungültig' || !folder.name || !contractData.primary_coach_id,
      icon: <SaveTwoTone />,
      fn: onStoreFolder,
      id: 1,
    },
    {
      caption: 'Löschen',
      disabled: status === 'ungültig' || !folder.name,
      icon: <DeleteTwoTone />,
      fn: onOpenDelete,
      id: 2,
    },
    { caption: null, disabled: null, icon: null, fn: null, id: 3 },
    {
      caption: 'Leistung',
      icon: <SVGStatus type={'performance'} status={fStatus.performance} />,
      fn: gotoPerformance,
      id: 4,
    },
    {
      caption: 'Anlagen',
      icon: <SVGStatus type={'installation'} status={fStatus.installation} />,
      fn: gotoInstallation,
      id: 5,
    },
    {
      caption: 'Korrelation',
      icon: <SVGStatus type={'correlation'} status={fStatus.correlation} />,
      fn: gotoCorrelation,
      id: 6,
    },
    {
      caption: 'Sicherheit',
      icon: <SVGStatus type={'security'} status={fStatus.security} />,
      fn: gotoSecurity,
      id: 7,
    },
    {
      caption: 'Wohlbefinden',
      icon: <SVGStatus type={'feeling'} status={fStatus.feeling} />,
      fn: gotoFeeling,
      id: 8,
    },
  ]

  const [avatarSrc, setAvatarSrc] = useState<string>(defaultImage)

  useEffect(() => {
    const value = folder?.image_url
    if (!value) {
      setAvatarSrc(defaultImage)
      return
    }

    if (String(value).startsWith('http')) {
      setAvatarSrc(String(value))
      return
    }

    const load = async () => {
      try {
        const url = await Storage.get(String(value), {
          level: 'public',
          customPrefix: { public: '' },
        })
        setAvatarSrc(String(url))
      } catch {
        setAvatarSrc(defaultImage)
      }
    }

    load()
  }, [folder?.image_url])

  return (
    <>
      {/* profile picture */}
      <DlgFolderImage
        id={Number(id)}
        onClose={onCloseFolderImage}
        onUpdate={onUpdateFolderImage}
        open={openProfilePicture}
      />
      {/* profile picture end */}

      {/* delete confirmation */}
      <DlgDelete
        onCancel={onCloseDelete}
        onConfirm={onConfirmDelete}
        open={confirmDialog}
      >
        <Typography>
          Löschen von Gebäude ID:
          {Number(id)} bestätigen
        </Typography>
      </DlgDelete>

      {/* add sensor */}
      <DlgSensorAdd
        folderSensors={folderSensors}
        onChange={onChangeFolderSensors}
        onClose={onCloseAddSensor}
        open={showModal}
      />
      {/* add sensor end */}

      {/* action menu */}
      <AM
        anchorEl={actionMenu}
        caption={'Gebäude'}
        open={Boolean(actionMenu)}
        onClose={onCloseAM}
        historyUrlTarget={'folder'}
        historyUrlId={Number(fId)}
      >
        {amData.map(item =>
          item.caption !== null ? (
            <AMItem
              caption={item.caption}
              icon={
                <Stack alignItems={'center'} sx={{ width: 25 }}>
                  {item.icon}
                </Stack>
              }
              key={item.id}
              onClick={item.fn}
            />
          ) : (
            <Divider key={item.id} />
          )
        )}
      </AM>
      {/* action menu end */}

      <KPage
        avatar={
          <Avatar
            onClick={onOpenFolderImage}
            src={avatarSrc}
            sx={{ cursor: 'pointer' }}
          >
            <AccountBalanceTwoTone />
          </Avatar>
        }
        action={
          <IconButton onClick={onOpenAM}>
            <MoreVertTwoTone />
          </IconButton>
        }
        subheader={!editMode ? 'Neues Gebäude' : `ID:${fId}`}
        title={`${folder.name}`}
      >
        <Tabs
          centered
          indicatorColor={'primary'}
          onChange={(event, newTab) => setTab(newTab)}
          sx={{ mt: 5, mb: 5 }}
          textColor={'primary'}
          value={tab}
        >
          <Tab icon={<AccountBalanceTwoTone />} label={'Stammdaten'} />

          <Tab
            icon={
              <Badge
                badgeContent={folderSensors?.length}
                color={'secondary'}
                overlap={'circular'}
              >
                <MemoryTwoTone />
              </Badge>
            }
            label={'Sensoren'}
          />

          <Tab
            icon={
              <Badge
                badgeContent={folderUsers?.length}
                color={'secondary'}
                overlap={'circular'}
              >
                <GroupTwoTone />
              </Badge>
            }
            label={'Benutzer'}
          />

          <Tab
            icon={
              <Badge
                badgeContent={
                  officeHours
                    ? String(officeHours.filter(f => f.closed === 0).length)
                    : null
                }
                color={'secondary'}
                overlap={'circular'}
              >
                <ScheduleTwoTone />
              </Badge>
            }
            label={'Betriebszeiten'}
          />
        </Tabs>

        {/* Stammdaten */}
        <Collapse in={tab === 0}>
          {NO_SENSOR_IN_OBJECT && (
            <Alert severity={'warning'}>
              Bitte mindestens 1 Sensor im Gebäude speichern, um mit der
              Konfiguration fortzufahren.
            </Alert>
          )}

          <Section title={'Meta'}>
            <KTextField
              error={!isValidLength(folder.name, 3)}
              label={'Name'}
              onChange={onChangeName}
              type={'text'}
              value={folder.name}
            />

            <KTextField
              label={'Kurzname'}
              onChange={onChangeShortName}
              type={'text'}
              value={folder.name_short}
            />

            {fPool.length > 0 && (
              <KSelect
                icon={<AccountBalanceTwoTone />}
                label={`Übergeordnetes Gebäude (parent_id:${folder.parent_id === 0 ? 'null' : folder.parent_id})`}
                onChange={onChangeParentFolder}
                value={folder.parent_id}
              >
                <MenuItem value={0}>Kein übergeordnetes Gebäude</MenuItem>

                {fPool
                  .filter(f => f.id !== id)
                  .map(item => {
                    const { name } = item.attributes

                    return (
                      <MenuItem key={name} value={item.id}>
                        {name}
                      </MenuItem>
                    )
                  })}
              </KSelect>
            )}
          </Section>

          {coachPool.length > 0 && (
            <Section title={'Coach'}>
              <KSelect
                error={!contractData?.primary_coach_id}
                icon={<FaceTwoTone />}
                label={`(ID:${
                  contractData?.primary_coach_id || 'Kein Coach gewählt'
                })`}
                onChange={onChangeCoach}
                value={Number(contractData?.primary_coach_id)}
              >
                {coachPool.map(item => {
                  const { firstname, lastname, is_active, entity_id } =
                    item.attributes

                  const USER_DEACTIVATED = ' (deaktiviert)'
                  const fullName = `${firstname} ${lastname}`
                  const disabled = is_active === 0

                  return (
                    <MenuItem
                      disabled={disabled}
                      key={fullName}
                      value={entity_id}
                    >
                      {fullName} {disabled && USER_DEACTIVATED} (ID:
                      {entity_id})
                    </MenuItem>
                  )
                })}
              </KSelect>
            </Section>
          )}

          <Section title={'Notizen'}>
            <KTextField
              label={'Notizen'}
              multiline
              onChange={onChangeDescription}
              minRows={5}
              value={contractData?.description}
            />
          </Section>

          <Section title={'Gebäudebestandteile'}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={folder.elevator === 1}
                  onClick={onToggleElevator}
                />
              }
              label={'Aufzug'}
              labelPlacement={'start'}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={folder.escalator === 1}
                  onClick={onToggleEscalator}
                />
              }
              label={'Rolltreppe'}
              labelPlacement={'start'}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={folder.total_consumption_available === 1}
                  onChange={onToggleConsumptionAvailable}
                />
              }
              label={'Gesamtverbrauch'}
              labelPlacement={'start'}
            />
          </Section>

          <Section title={'Adresse'}>
            <KTextField
              icon={<EditLocationTwoTone />}
              label={'Anschrift'}
              onChange={onChangeStreet}
              type={'text'}
              value={folder.street}
            />

            <KTextField
              icon={<EditLocationTwoTone />}
              label={'PLZ'}
              onChange={onChangeZip}
              type={'number'}
              value={folder.zip}
            />

            <KTextField
              icon={<EditLocationTwoTone />}
              label={'Ort'}
              onChange={onChangeCity}
              type={'text'}
              value={folder.city}
            />

            <KSelect
              icon={<EditLocationTwoTone />}
              label={'Land'}
              onChange={onChangeCountry}
              value={folder.country}
            >
              {[
                { label: 'Deutschland', value: 'DEU' },
                { label: 'Österreich', value: 'AUS' },
                { label: 'Schweiz', value: 'CHE' },
              ].map(item => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </KSelect>
          </Section>

          <Section title={'Geographische Koordinaten'}>
            <HStack justifyContent={'space-evenly'}>
              <KTextField
                icon={<EditLocationTwoTone />}
                label={'Breitengrad'}
                onChange={onChangeLatitude}
                type={'number'}
                unit={'°'}
                value={folder.latitude}
              />

              <KTextField
                icon={<EditLocationTwoTone />}
                label={'Längengrad'}
                onChange={onChangeLongitude}
                type={'number'}
                unit={'°'}
                value={folder.longitude}
              />
            </HStack>
          </Section>

          <Section title={'Verbrauchsbezug'}>
            <KSelect
              label={'Mitarbeiter / Zimmer'}
              onChange={onChangeRoomsMode}
              value={folder.rooms_mode}
            >
              {[
                { label: 'Mitarbeiter', value: 'employees' },
                { label: 'Zimmer', value: 'rooms' },
              ].map(item => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </KSelect>

            <KTextField
              label={'Anzahl'}
              onChange={onChangeEmployees}
              type={'number'}
              unit={'Personen'}
              value={folder.number_of_employees}
            />
          </Section>

          <Section title={'Flächendaten'}>
            <KTextField
              label={'Fläche'}
              onChange={onChangeAreaInSqm}
              type={'number'}
              unit={'m²'}
              value={folder.area_in_sqm}
            />

            <KTextField
              label={'Allgemeine Nutzungsfläche'}
              onChange={onChangeUsableArea}
              type={'number'}
              unit={'m²'}
              value={folder.usable_area}
            />

            <KTextField
              label={'Vermietete Nutzungsfläche'}
              onChange={onChangeHiredOutArea}
              type={'number'}
              unit={'m²'}
              value={folder.hired_out_area}
            />

            <KTextField
              label={'Unklimatisiert'}
              onChange={onChangeNoneTemperatureControlledArea}
              type={'number'}
              unit={'m²'}
              value={folder.none_temperature_controlled_area}
            />

            <KTextField
              label={'Klimatisiert'}
              onChange={onChangeTemperatureControlledArea}
              type={'number'}
              unit={'m²'}
              value={folder.temperature_controlled_area}
            />
          </Section>

          <Section
            title={'Anlagen'}
            actionArea={
              <IconButton onClick={onAddInstallation}>
                <AddTwoTone />
              </IconButton>
            }
          >
            {installations.map((item, key) => {
              const { delete_installation, name } = item

              const onChangeName = event => {
                const newInstall = installations.slice()
                newInstall[key]['name'] = event.target.value

                setInstallations(newInstall)
              }

              const onDeleteInstallation = () => deleteInstallation(item, key)

              if (delete_installation === undefined) {
                return (
                  <HStack key={name}>
                    <KTextField
                      label={'Name'}
                      onChange={onChangeName}
                      value={name}
                    />

                    <IconButton onClick={onDeleteInstallation}>
                      <Tooltip placement={'bottom-start'} title={'Löschen'}>
                        <CancelTwoTone />
                      </Tooltip>
                    </IconButton>
                  </HStack>
                )
              }

              return true
            })}

            <KTextField
              label={'Nutzungsart'}
              onChange={onChangeKindOfUse}
              value={folder.kind_of_use}
            />

            <KSelect
              label={'Klimatisierung'}
              onChange={onChangeAirConditioning}
              value={folder.air_conditioning}
            >
              {[
                { label: 'Klimatisiert', value: 'temperature_controlled' },
                {
                  label: 'Unklimatisiert',
                  value: 'not_temperature_controlled',
                },
              ].map(item => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </KSelect>
          </Section>

          <Section title={'Vertragslaufzeit'}>
            <HStack>
              <KTextField
                label={'Beginn'}
                onChange={onChangeContractStart}
                type={'date'}
                value={contractData?.date_begin}
              />

              <KTextField
                label={'Ablauf'}
                onChange={onChangeContractEnd}
                type={'date'}
                value={contractData?.date_expire}
              />
            </HStack>

            <KTextField
              disabled
              error={status === 'ungültig'}
              label={'Laufzeit'}
              unit={'Tage'}
              value={String(duration)}
            />

            <Alert severity={getSeverity(status)} sx={{ m: 1 }}>
              Vertragsstatus: {status}
            </Alert>
          </Section>
        </Collapse>

        {/* Sensoren */}
        <Collapse in={tab === 1}>
          <Section
            title={`${
              [...new Set(folderSensors.map(item => item.device_id))].length
            } Geräte | ${folderSensors.length} Sensoren`}
            actionArea={
              <IconButton onClick={onOpenAddSensor}>
                <AddTwoTone />
              </IconButton>
            }
          >
            <Box
              display={'grid'}
              gap={3}
              gridTemplateColumns={{
                xs: '1fr',
                sm: 'repeat(1, 1fr)',
                md: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
                xl: 'repeat(4, 1fr)',
              }}
            >
              {[...new Set(folderSensors.map(item => item.name))]
                .sort(SORT)
                .map(item => {
                  const CURRENT = f => f.name === item

                  const sensorTypes = [
                    ...new Set(folderSensors.filter(CURRENT).sort()),
                  ]

                  const data = folderSensors.find(CURRENT)

                  const onDelete = () =>
                    setFolderSensors(
                      folderSensors.filter(
                        f => Number(f.id) !== Number(data.id)
                      )
                    )

                  return (
                    <CardFolderSensor
                      id={data.entity_id}
                      onDelete={onDelete}
                      sensorTypes={sensorTypes}
                      key={item}
                    />
                  )
                })}
            </Box>
          </Section>
        </Collapse>

        {/* Benutzer */}
        <Collapse in={tab === 2}>
          <Section title={`${folderUsers.length} Benutzer`}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 5 }}>
              {folderUsers
                .sort((a, b) => {
                  const nameA = String(a.firstname).toUpperCase()
                  const nameB = String(b.firstname).toUpperCase()

                  if (nameA < nameB) return -1
                  if (nameA > nameB) return 1

                  return 0
                })
                .map(user => {
                  const { firstname, image_url, label, lastname } = user

                  const fullName = `${firstname} ${lastname}`

                  const onDelete = () =>
                    setFolderUsers(fu => fu.filter(f => f.id !== user.id))

                  const color = roleFilter === label ? 'secondary' : 'primary'

                  return (
                    <Chip
                      avatar={
                        image_url ? (
                          <Avatar src={image_url} />
                        ) : (
                          <Avatar>{getInitials(fullName)}</Avatar>
                        )
                      }
                      color={color}
                      key={user.id}
                      label={fullName}
                      onDelete={onDelete}
                      onMouseDown={event => event.stopPropagation()}
                      sx={{ m: 1 }}
                    />
                  )
                })}
            </Box>

            <FormControl
              fullWidth
              sx={{ alignItems: 'center', justifyContent: 'center', mb: 5 }}
              variant={'outlined'}
            >
              <ToggleButtonGroup
                onChange={(event, value) => setRoleFilter(value[0])}
              >
                {[
                  { label: 'Alle', selected: roleFilter === '', value: '' },
                  {
                    label: LC.USERROLE_ADMIN,
                    selected: roleFilter === LC.USERROLE_ADMIN,
                    value: LC.USERROLE_ADMIN,
                  },
                  {
                    label: LC.USERROLE_COACH,
                    selected: roleFilter === LC.USERROLE_COACH,
                    value: LC.USERROLE_COACH,
                  },
                  {
                    label: LC.USERROLE_USER,
                    selected: roleFilter === LC.USERROLE_USER,
                    value: LC.USERROLE_USER,
                  },
                  {
                    label: LC.USERROLE_GUEST,
                    selected: roleFilter === LC.USERROLE_GUEST,
                    value: LC.USERROLE_GUEST,
                  },
                ].map(item => (
                  <ToggleButton
                    key={item.value}
                    selected={item.selected}
                    value={item.value}
                  >
                    {item.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </FormControl>

            <KTextField
              label={'Benutzer oder Email suchen'}
              onChange={event => setDisplayFilter(event.target.value)}
              value={displayFilter}
            />

            <FormControl sx={{ mb: 2 }} fullWidth variant={'outlined'}>
              <List sx={{ maxHeight: 420, height: 420, overflow: 'auto' }}>
                {uPool
                  .filter(f =>
                    roleFilter ? f.attributes.label === roleFilter : f
                  )
                  .filter(f =>
                    displayFilter
                      ? String(
                          `${f.attributes.firstname} ${f.attributes.lastname}`
                        )
                          .toLowerCase()
                          .includes(displayFilter.toLowerCase()) ||
                        String(f.attributes.email)
                          .toLowerCase()
                          .includes(displayFilter.toLowerCase())
                      : f
                  )
                  .filter(FILTER_ACTIVE_USER)
                  .map(user => {
                    const { email, firstname, image_url, label, lastname } =
                      user.attributes

                    const fullName = `${firstname} ${lastname}`
                    const userProps = `ID: ${user.id} | ${label} | ${email}`

                    const onChangeFolderUsers = () => {
                      if (folderUsers.map(u => u.id).includes(user.id))
                        setFolderUsers(fu => fu.filter(f => f.id !== user.id))
                      else setFolderUsers([...folderUsers, user])
                    }

                    const checked = folderUsers
                      .map(item => item.id)
                      .includes(user.id)

                    return (
                      <ListItemButton
                        key={fullName}
                        onClick={onChangeFolderUsers}
                      >
                        <ListItemAvatar>
                          {image_url ? (
                            <Avatar alt={''} src={image_url} />
                          ) : (
                            <Avatar alt={''}>{getInitials(fullName)}</Avatar>
                          )}
                        </ListItemAvatar>

                        <ListItemText
                          primary={fullName}
                          secondary={userProps}
                        />

                        <Checkbox
                          checked={checked}
                          edge={'end'}
                          onChange={onChangeFolderUsers}
                        />
                      </ListItemButton>
                    )
                  })}
              </List>
            </FormControl>
          </Section>
        </Collapse>

        {/* Betriebszeiten */}
        <Collapse in={tab === 3}>
          <Section title={'Betriebszeiten'}>
            {[
              'Montag',
              'Dienstag',
              'Mittwoch',
              'Donnerstag',
              'Freitag',
              'Samstag',
              'Sonntag',
            ].map((item, key) => {
              const record = officeHours?.[key]
                ? officeHours[key]
                : {
                    closed: 1,
                    day_of_week: key + 1,
                    end_time: '00:00:00',
                    start_time: '00:00:00',
                  }

              const IS_OPEN = record.closed === 0
              const IS_CLOSED = record.closed === 1

              const onChangeFrom = event => {
                record.start_time = event.target.value

                changeOfficeHours(record, key)
              }

              const onChangeTo = event => {
                record.end_time = event.target.value

                changeOfficeHours(record, key)
              }

              const onToggleIsClosed = () => {
                record.closed = IS_CLOSED ? 0 : 1

                changeOfficeHours(record, key)
              }

              const startTime = normalizeTime(record.start_time)
              const endTime = normalizeTime(record.end_time)

              return (
                <HStack alignItems={'center'} key={item}>
                  <Typography sx={{ width: 0.25 }}>{item}</Typography>

                  <KTextField
                    disabled={IS_CLOSED}
                    label={'von'}
                    onChange={onChangeFrom}
                    type={'time'}
                    value={startTime}
                  />

                  <KTextField
                    disabled={IS_CLOSED}
                    label={'bis'}
                    onChange={onChangeTo}
                    type={'time'}
                    value={endTime}
                  />

                  <Switch checked={IS_OPEN} onChange={onToggleIsClosed} />
                </HStack>
              )
            })}
          </Section>
        </Collapse>
      </KPage>
    </>
  )
}
